// 碎片渲染 - 运行时切割图片并应用六边形蒙版

import type { Cell, PieceData, GridConfig } from '../types.js';
import { cellToPixel, drawHexPath, computeBoundingBox, getPixelBoundingBox, getHexVertices } from '../utils/hexUtils.js';

export class PieceRenderer {
  generatePieceImage(
    piece: PieceData,
    fullImage: HTMLImageElement,
    grid: GridConfig
  ): HTMLCanvasElement {
    const { hexSize, cropRegion, imageSize } = grid as any;
    // 统一使用 config.imageSize 坐标空间
    const refW = imageSize?.width ?? fullImage.width;
    const refH = imageSize?.height ?? fullImage.height;
    // 如果实际图片和 config 尺寸不同，需要缩放坐标
    const imgScaleX = fullImage.width / refW;
    const imgScaleY = fullImage.height / refH;
    // cropRegion.left/top 就是 config.imageSize 坐标系的像素值
    const cropLeft = cropRegion.left;
    const cropTop = cropRegion.top;

    const cells = piece.cells;
    const bbox = getPixelBoundingBox(cells, hexSize, cropLeft, cropTop);

    // 添加一些padding
    const padding = hexSize * 0.3;
    const drawX = bbox.minX - padding;
    const drawY = bbox.minY - padding;
    const drawW = (bbox.maxX - bbox.minX) + padding * 2;
    const drawH = (bbox.maxY - bbox.minY) + padding * 2;

    // ⚠️ 每次都创建新 canvas —— 否则所有 piece 引用同一个 canvas，后画的覆盖先画的！
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(drawW);
    canvas.height = Math.ceil(drawH);
    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // 对每个cell：绘制六边形蒙版 + 切割图片区域 + 白色描边
    for (const cell of cells) {
      const { x, y } = cellToPixel(cell.col, cell.row, hexSize, cropLeft, cropTop);

      // 局部坐标（piece canvas 空间，和 config.imageSize 一致）
      const localCx = x - drawX;
      const localCy = y - drawY;

      // 保存上下文
      ctx.save();

      // 1. 创建六边形裁剪区域（config 坐标）
      drawHexPath(ctx, localCx, localCy, hexSize);
      ctx.clip();

      // 2. 从原图切割对应区域并绘制
      // 原图中这个六边形中心的位置（实际图片像素，需要按比例缩放）
      const sx = (x - hexSize) * imgScaleX;
      const sy = (y - hexSize) * imgScaleY;
      const ssw = hexSize * 2 * imgScaleX;
      const ssh = hexSize * 2 * imgScaleY;
      ctx.drawImage(
        fullImage,
        Math.max(0, sx), Math.max(0, sy),
        ssw, ssh,
        localCx - hexSize, localCy - hexSize,
        hexSize * 2, hexSize * 2
      );

      ctx.restore();

      // 3. 绘制白色描边（在clip外面画）
      ctx.save();
      drawHexPath(ctx, localCx, localCy, hexSize);
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.restore();
    }

    return canvas;
  }
}

// 计算碎片在碎片区的显示尺寸
export function computePieceDisplaySize(
  piece: PieceData,
  hexSize: number,
  scale: number = 0.55
): { width: number; height: number } {
  const bbox = computeBoundingBox(piece.cells);
  const cols = bbox.maxCol - bbox.minCol + 1;
  const rows = bbox.maxRow - bbox.minRow + 1;

  // 估算像素尺寸
  const pixelW = cols * hexSize * 1.5 + hexSize * 0.5;
  const pixelH = rows * hexSize * Math.sqrt(3) + hexSize * Math.sqrt(3);

  return {
    width: pixelW * scale,
    height: pixelH * scale,
  };
}
